/**
 * GoProvalo Homepage
 * File: assets/goprovalo-homepage.js
 *
 * Responsibilities:
 * - Handle homepage property searches
 * - Validate and normalize search values
 * - Support popular-search links
 * - Handle internal smooth-scroll links
 * - Add viewport reveal behavior
 * - Respect reduced-motion preferences
 * - Support Shopify Theme Editor section reloads
 * - Load featured properties from the GoProvalo catalog API
 */

(() => {
  'use strict';

  const SELECTORS = {
    homepage: '.gp-home',
    searchForm: '[data-gp-property-search]',
    searchInput: '.gp-search__input',
    popularSearch: '.gp-popular-searches__link',
    internalLink: 'a[href^="#"]',
    featuredGrid: '#gp-featured-properties-grid',
    revealElements: [
      '.gp-hero__eyebrow',
      '.gp-hero__title',
      '.gp-hero__description',
      '.gp-search',
      '.gp-popular-searches',
      '.gp-hero__actions',
      '.gp-hero__trust',
      '.gp-property-visual',
      '.gp-stat'
    ].join(',')
  };

  const CSS_CLASSES = {
    initialized: 'gp-home--initialized',
    searchError: 'gp-search--error',
    reveal: 'gp-reveal',
    revealed: 'gp-reveal--visible'
  };

  /**
   * Returns true when the visitor prefers reduced motion.
   *
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
  }

  /**
   * Safely trims and normalizes a search value.
   *
   * This intentionally keeps spaces, hyphens and slash characters because
   * valid case numbers and addresses may contain them.
   *
   * @param {string} value
   * @returns {string}
   */
  function normalizeSearchValue(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Returns the configured Property Catalog URL.
   *
   * @param {HTMLElement} homepage
   * @returns {string}
   */
  function getCatalogUrl(homepage) {
    const configuredUrl =
      homepage.dataset.propertyCatalogUrl || '';

    return configuredUrl.trim();
  }

  /**
   * Briefly displays a visual error state on the search box.
   *
   * @param {HTMLFormElement} form
   * @param {HTMLInputElement} input
   */
  function showSearchError(form, input) {
    form.classList.add(CSS_CLASSES.searchError);
    input.setAttribute('aria-invalid', 'true');

    window.setTimeout(() => {
      form.classList.remove(CSS_CLASSES.searchError);
      input.removeAttribute('aria-invalid');
    }, 1600);
  }

  /**
   * Builds a catalog URL while preserving any existing query parameters.
   *
   * @param {string} catalogUrl
   * @param {string} searchValue
   * @returns {string}
   */
  function buildSearchUrl(catalogUrl, searchValue) {
    const url = new URL(catalogUrl, window.location.origin);

    url.searchParams.set('search', searchValue);

    return url.toString();
  }

  /**
   * Handles the main property search form.
   *
   * @param {HTMLElement} homepage
   */
  function initializePropertySearch(homepage) {
    const form = homepage.querySelector(
      SELECTORS.searchForm
    );

    if (!form || form.dataset.gpInitialized === 'true') {
      return;
    }

    const input = form.querySelector(
      SELECTORS.searchInput
    );

    if (!input) {
      return;
    }

    form.dataset.gpInitialized = 'true';

    form.addEventListener('submit', event => {
      event.preventDefault();

      const searchValue = normalizeSearchValue(
        input.value
      );

      const catalogUrl = getCatalogUrl(homepage);

      if (!catalogUrl) {
        console.error(
          'GoProvalo homepage: Property Catalog URL is not configured.'
        );

        showSearchError(form, input);
        input.focus();

        return;
      }

      if (!searchValue) {
        window.location.assign(catalogUrl);
        return;
      }

      input.value = searchValue;

      const destinationUrl = buildSearchUrl(
        catalogUrl,
        searchValue
      );

      window.location.assign(destinationUrl);
    });

    input.addEventListener('input', () => {
      if (form.classList.contains(CSS_CLASSES.searchError)) {
        form.classList.remove(CSS_CLASSES.searchError);
        input.removeAttribute('aria-invalid');
      }
    });

    input.addEventListener('keydown', event => {
      if (event.key !== 'Escape') {
        return;
      }

      input.value = '';
      input.focus();
    });
  }

  /**
   * Ensures popular-search links use the currently configured catalog URL.
   *
   * @param {HTMLElement} homepage
   */
  function initializePopularSearches(homepage) {
    const links = homepage.querySelectorAll(
      SELECTORS.popularSearch
    );

    const catalogUrl = getCatalogUrl(homepage);

    if (!catalogUrl || !links.length) {
      return;
    }

    links.forEach(link => {
      if (link.dataset.gpInitialized === 'true') {
        return;
      }

      link.dataset.gpInitialized = 'true';

      link.addEventListener('click', event => {
        const currentUrl = new URL(
          link.href,
          window.location.origin
        );

        const searchValue = normalizeSearchValue(
          currentUrl.searchParams.get('search')
        );

        if (!searchValue) {
          return;
        }

        event.preventDefault();

        const destinationUrl = buildSearchUrl(
          catalogUrl,
          searchValue
        );

        window.location.assign(destinationUrl);
      });
    });
  }

  /**
   * Handles internal section links.
   *
   * @param {HTMLElement} homepage
   */
  function initializeSmoothScroll(homepage) {
    const links = homepage.querySelectorAll(
      SELECTORS.internalLink
    );

    links.forEach(link => {
      if (link.dataset.gpScrollInitialized === 'true') {
        return;
      }

      const href = link.getAttribute('href');

      if (!href || href === '#') {
        return;
      }

      link.dataset.gpScrollInitialized = 'true';

      link.addEventListener('click', event => {
        let target;

        try {
          target = document.querySelector(href);
        } catch (error) {
          return;
        }

        if (!target) {
          return;
        }

        event.preventDefault();

        target.scrollIntoView({
          behavior: prefersReducedMotion()
            ? 'auto'
            : 'smooth',
          block: 'start'
        });

        if (
          window.history &&
          typeof window.history.replaceState === 'function'
        ) {
          window.history.replaceState(
            null,
            '',
            href
          );
        }
      });
    });
  }

  /**
   * Adds reveal classes when elements enter the viewport.
   *
   * @param {HTMLElement} homepage
   */
  function initializeRevealAnimations(homepage) {
    const elements = homepage.querySelectorAll(
      SELECTORS.revealElements
    );

    if (!elements.length) {
      return;
    }

    if (
      prefersReducedMotion() ||
      !('IntersectionObserver' in window)
    ) {
      elements.forEach(element => {
        element.classList.add(
          CSS_CLASSES.revealed
        );
      });

      return;
    }

    elements.forEach((element, index) => {
      if (element.dataset.gpRevealInitialized === 'true') {
        return;
      }

      element.dataset.gpRevealInitialized = 'true';
      element.classList.add(CSS_CLASSES.reveal);

      if (element.classList.contains('gp-stat')) {
        const statisticIndex = Array.from(
          element.parentElement.children
        ).indexOf(element);

        element.style.setProperty(
          '--gp-reveal-delay',
          `${Math.max(statisticIndex, 0) * 90}ms`
        );
      } else {
        element.style.setProperty(
          '--gp-reveal-delay',
          `${Math.min(index * 45, 270)}ms`
        );
      }
    });

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add(
            CSS_CLASSES.revealed
          );

          observer.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12
      }
    );

    elements.forEach(element => {
      if (
        !element.classList.contains(
          CSS_CLASSES.revealed
        )
      ) {
        observer.observe(element);
      }
    });
  }

  /**
   * Adds keyboard accessibility classes.
   *
   * @param {HTMLElement} homepage
   */
  function initializeKeyboardFocus(homepage) {
    const clickableElements = homepage.querySelectorAll(
      'a, button, input'
    );

    clickableElements.forEach(element => {
      element.addEventListener(
        'keydown',
        event => {
          if (event.key !== 'Tab') {
            return;
          }

          homepage.classList.add(
            'gp-home--keyboard-navigation'
          );
        },
        { once: true }
      );
    });

    homepage.addEventListener(
      'mousedown',
      () => {
        homepage.classList.remove(
          'gp-home--keyboard-navigation'
        );
      },
      { passive: true }
    );
  }

  /**
   * Escapes dynamic values before inserting them into HTML.
   *
   * @param {unknown} value
   * @returns {string}
   */
  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Returns the first usable value from a list.
   *
   * @param {...unknown} values
   * @returns {unknown}
   */
  function firstValue(...values) {
    return values.find(value => {
      return (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ''
      );
    }) ?? '';
  }

  /**
   * Formats a property date.
   *
   * @param {unknown} value
   * @returns {string}
   */
  function formatPropertyDate(value) {
    if (!value) {
      return 'To be announced';
    }

    const normalizedValue = String(value).trim();
    const date = new Date(normalizedValue);

    if (Number.isNaN(date.getTime())) {
      return normalizedValue;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }

  /**
   * Formats a property value as US currency.
   *
   * @param {unknown} value
   * @returns {string}
   */
  function formatPropertyCurrency(value) {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ''
    ) {
      return 'Not available';
    }

    const normalized = Number(
      String(value).replace(/[^0-9.-]/g, '')
    );

    if (!Number.isFinite(normalized)) {
      return String(value);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(normalized);
  }

  /**
   * Extracts the property list from supported API response structures.
   *
   * @param {unknown} payload
   * @returns {Array<Record<string, unknown>>}
   */
  function getPropertyList(payload) {
    if (!payload || typeof payload !== 'object') {
      return [];
    }

    const possibleLists = [
      payload.properties,
      payload.data,
      payload.results,
      payload.items,
      payload.data?.properties,
      payload.data?.results,
      payload.data?.items
    ];

    return possibleLists.find(Array.isArray) || [];
  }

  /**
   * Creates the HTML for one featured property.
   *
   * @param {Record<string, unknown>} property
   * @returns {string}
   */
  function renderFeaturedProperty(property) {
    const caseNumber = firstValue(
      property.CaseNumber,
      property.case_number,
      property.caseNumber
    );

    const address = firstValue(
      property.Address,
      property.address,
      'Address unavailable'
    );

    const city = firstValue(
      property.City,
      property.city
    );

    const zip = firstValue(
      property.Zip,
      property.ZIP,
      property.zip
    );

    const status = firstValue(
      property.CurrentStatus,
      property.current_status,
      property.status,
      'Available'
    );

    const saleDate = firstValue(
      property.SaleDate,
      property.sale_date
    );

    const assessedValue = firstValue(
    property.County_Market_Value,
    property.county_market_value,
    property.AssessedValue,
    property.assessed_value,
    property['Assessed Value']
    );

    const lat = firstValue(
      property.Lat,
      property.lat,
      property.latitude
    );

    const lon = firstValue(
      property.Lon,
      property.lon,
      property.lng,
      property.longitude
    );

    const location = [city, zip]
      .filter(value => String(value).trim() !== '')
      .join(', ');

    const detailsUrl =
      `/pages/property-details?case_number=${encodeURIComponent(
        String(caseNumber)
      )}`;

    const imageUrl =
      lat && lon
        ? `https://api.goprovalo.com/streetview.php?lat=${encodeURIComponent(
            String(lat)
          )}&lon=${encodeURIComponent(String(lon))}`
        : '';

    const imageMarkup = imageUrl
      ? `
          <img
            src="${escapeHtml(imageUrl)}"
            alt="${escapeHtml(address)}"
            class="gp-featured-property-card__image"
            loading="lazy"
            width="720"
            height="460"
          >
        `
      : `
          <div class="gp-featured-property-card__placeholder">
            Property image unavailable
          </div>
        `;

    return `
      <article class="gp-featured-property-card">
        <a
          href="${escapeHtml(detailsUrl)}"
          class="gp-featured-property-card__image-link"
          aria-label="View ${escapeHtml(address)}"
        >
          ${imageMarkup}

          <span class="gp-featured-property-card__status">
            ${escapeHtml(status)}
          </span>
        </a>

        <div class="gp-featured-property-card__body">
          <p class="gp-featured-property-card__location">
            ${escapeHtml(location || 'Miami-Dade County')}
          </p>

          <h3 class="gp-featured-property-card__title">
            <a href="${escapeHtml(detailsUrl)}">
              ${escapeHtml(address)}
            </a>
          </h3>

          <div class="gp-featured-property-card__metrics">
            <div>
              <span>Auction date</span>
              <strong>
                ${escapeHtml(formatPropertyDate(saleDate))}
              </strong>
            </div>

            <div>
              <span>County Market Value</span>
              <strong>
                ${escapeHtml(
                  formatPropertyCurrency(assessedValue)
                )}
              </strong>
            </div>
          </div>

          <a
            href="${escapeHtml(detailsUrl)}"
            class="gp-featured-property-card__link"
          >
            <span>View Property</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </article>
    `;
  }

  /**
   * Loads featured properties from the catalog API.
   *
   * @param {HTMLElement} homepage
   */
  async function initializeFeaturedProperties(homepage) {
    console.log('Initializing Featured Properties...');

    const grid = document.querySelector(
      SELECTORS.featuredGrid
    );
    console.log('Grid:', grid);
    if (!grid || grid.dataset.gpLoaded === 'true') {
      return;
    }

    grid.dataset.gpLoaded = 'true';

    const apiUrl = new URL(
      'https://api.goprovalo.com/property_catalog_api.php'
    );

    apiUrl.searchParams.set('page', '1');
    apiUrl.searchParams.set('limit', '6');
    apiUrl.searchParams.set('status', 'Accepting Proxy');

    grid.setAttribute('aria-busy', 'true');

    grid.innerHTML = `
      <div class="gp-featured-properties__loading">
        <span class="gp-spinner" aria-hidden="true"></span>
        <span>Loading featured properties...</span>
      </div>
    `;

    try {
      const response = await fetch(apiUrl.toString(), {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Catalog request failed with status ${response.status}.`
        );
      }

      const payload = await response.json();

      if (payload.success === false) {
        throw new Error(
          payload.message || 'The catalog API returned an error.'
        );
      }

      const properties = getPropertyList(payload)
        .slice(0, 6);

      if (!properties.length) {
        grid.innerHTML = `
          <div class="gp-featured-properties__empty">
            No featured properties are available right now.
          </div>
        `;

        return;
      }

      grid.innerHTML = properties
        .map(renderFeaturedProperty)
        .join('');
    } catch (error) {
      console.error(
        'Unable to load featured properties:',
        error
      );

      grid.innerHTML = `
        <div class="gp-featured-properties__error">
          <p>
            Featured properties could not be loaded right now.
          </p>

          <a href="/pages/properties">
            Browse the full property catalog
          </a>
        </div>
      `;
    } finally {
      grid.removeAttribute('aria-busy');
    }
  }

  /**
   * Initializes one homepage section.
   *
   * @param {HTMLElement} homepage
   */
  function initializeHomepage(homepage) {
    if (!(homepage instanceof HTMLElement)) {
      return;
    }

    initializePropertySearch(homepage);
    initializePopularSearches(homepage);
    initializeSmoothScroll(homepage);
    initializeRevealAnimations(homepage);
    initializeKeyboardFocus(homepage);
    initializeFeaturedProperties(homepage);

    homepage.classList.add(
      CSS_CLASSES.initialized
    );
  }

  /**
   * Initializes all homepage sections currently in the document.
   *
   * @param {ParentNode} context
   */
  function initializeAllHomepages(context = document) {
    const homepages = context.querySelectorAll(
      SELECTORS.homepage
    );

    homepages.forEach(initializeHomepage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAllHomepages(),
      { once: true }
    );
  } else {
    initializeAllHomepages();
  }

  document.addEventListener(
    'shopify:section:load',
    event => {
      initializeAllHomepages(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:reorder',
    () => {
      initializeAllHomepages();
    }
  );
})();